import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  title?: string;
  subtitle?: string;
  topic?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const HeaderTitleSlide1 = {
  id: "header-title1-slide",
  name: "Header Title Slide (Split)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "topic",
      label: "Topic",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      topic = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div className="w-full h-full relative overflow-hidden text-white">
        {/* Split Background */}
        <div className="absolute inset-0 flex">
          <div className="w-1/3" style={{ background: bgColor }}></div>
          <div className="w-2/3 bg-gray-900"></div>
        </div>

        {/* Overlay Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.2) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Content Container */}
        <div className="relative z-10 flex h-full">
          {/* Left Panel */}
          <div className="w-1/3 p-12 flex flex-col justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-1 h-8 bg-white/30 rounded-full"></div>
                <EditableDiv
                  text={topic}
                  onEdit={handleEdit("topic")}
                  className="text-sm font-medium"
                  placeholder="Enter topic..."
                />
              </div>
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-full h-1 bg-white/10 rounded-full overflow-hidden"
                >
                  <div
                    className="h-full bg-white/30 rounded-full animate-progress"
                    style={{
                      width: `${(3 - i) * 30}%`,
                      animationDelay: `${i * 0.2}s`,
                    }}
                  ></div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel */}
          <div className="w-2/3 p-16 flex flex-col justify-center">
            {/* Decorative Corner Elements */}
            <div className="absolute top-12 right-12">
              <div className="flex space-x-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-white/30"
                    style={{
                      animation: `pulse ${1 + i * 0.2}s infinite alternate ease-in-out`,
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Main Content Container */}
            <div className="max-w-3xl space-y-12">
              {/* Title Container */}
              <div className="relative">
                {/* Background Glow */}
                <div className="absolute -inset-4 bg-white/5 blur-2xl rounded-lg"></div>

                {/* Title */}
                <EditableDiv
                  text={title}
                  onEdit={handleEdit("title")}
                  className="relative text-7xl font-black tracking-tight leading-none animate-slide-right"
                  style={{
                    background:
                      "linear-gradient(to right, white, rgba(255,255,255,0.7))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                  placeholder="Enter title..."
                />
              </div>

              {/* Animated Line with Dot */}
              <div className="flex items-center space-x-4">
                <div className="w-24 h-1 bg-gradient-to-r from-white/80 to-transparent animate-width"></div>
                <div className="w-3 h-3 rounded-full bg-white/50 animate-pulse"></div>
              </div>

              {/* Subtitle with Enhanced Typography */}
              <div className="relative">
                <EditableDiv
                  text={subtitle}
                  onEdit={handleEdit("subtitle")}
                  className="text-3xl font-light text-white/90 leading-relaxed animate-slide-right animation-delay-300 max-w-2xl"
                  placeholder="Enter subtitle..."
                />
                {/* Subtle Underline */}
                <div className="absolute -bottom-4 left-0 w-24 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
              </div>
            </div>

            {/* Bottom Decorative Elements */}
            <div className="absolute bottom-12 right-12">
              <div className="flex items-center space-x-6">
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                <div className="w-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-32 h-32 border border-white/10 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                transform: "translate(-50%, -50%)",
                animation: `float-${i} ${10 + i * 2}s infinite linear`,
              }}
            ></div>
          ))}
        </div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes slide-right {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes width {
    from { width: 0; }
    to { width: 6rem; }
  }

  @keyframes progress {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }

  ${[...Array(5)]
    .map(
      (_, i) => `
    @keyframes float-${i} {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
  `,
    )
    .join("\n")}

  .animate-slide-right {
    animation: slide-right 0.8s ease-out forwards;
  }

  .animate-width {
    animation: width 0.8s ease-out forwards;
  }

  .animate-progress {
    animation: progress 1s ease-out forwards;
  }

  .animation-delay-300 {
    animation-delay: 300ms;
  }
`;
document.head.appendChild(style);

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default HeaderTitleSlide1;
